import { useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import SeoHead, { BASE_URL } from "@/components/feature/SeoHead";
import { dubaiFactories } from "@/mocks/dubaiFactories";
import { saudiFactories } from "@/mocks/saudiFactories";
import { qatarFactories } from "@/mocks/qatarFactories";
import { Factory } from "@/components/feature/FactoryCard";

interface FlatProduct {
  productId: string;
  factoryId: string;
  factoryName: string;
  factoryCity: string;
  factoryVerified: boolean;
  factoryDropshipping: boolean;
  factoryRating: number;
  country: "dubai" | "saudi" | "qatar";
  name: string;
  category: string;
  samplePrice: number;
  bulkPrice: number;
  moq: number;
  image: string;
}

const ALL_CATEGORIES = [
  "Electronics", "Machinery", "Textiles & Clothing", "Food & Beverage",
  "Jewellery & Luxury", "Furniture & Home Decor", "Chemicals & Plastics",
  "Packaging", "Auto Parts", "Beauty & Fragrance", "Construction Materials",
  "Safety Equipment", "Smart Devices & IoT",
];

const COUNTRY_META = {
  dubai: { label: "Dubai", flag: "🇦🇪", color: "bg-amber-100 text-amber-700" },
  saudi: { label: "Saudi Arabia", flag: "🇸🇦", color: "bg-emerald-100 text-emerald-700" },
  qatar: { label: "Qatar", flag: "🇶🇦", color: "bg-sky-100 text-sky-700" },
};

const flattenFactories = (factories: Factory[], country: "dubai" | "saudi" | "qatar"): FlatProduct[] =>
  factories.flatMap((f) =>
    f.products.map((p) => ({
      productId: p.id,
      factoryId: f.id,
      factoryName: f.name,
      factoryCity: f.city,
      factoryVerified: f.verified,
      factoryDropshipping: f.dropshipping,
      factoryRating: f.rating,
      country,
      name: p.name,
      category: p.category,
      samplePrice: p.samplePrice,
      bulkPrice: p.bulkPrice,
      moq: p.moq,
      image: p.image,
    }))
  );

const allProducts: FlatProduct[] = [
  ...flattenFactories(dubaiFactories as unknown as Factory[], "dubai"),
  ...flattenFactories(saudiFactories as unknown as Factory[], "saudi"),
  ...flattenFactories(qatarFactories as unknown as Factory[], "qatar"),
];

type SortOption = "relevance" | "price-asc" | "price-desc" | "rating";

const GlobalSearchPage = () => {
  const [searchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get("q") || "");
  const [inputVal, setInputVal] = useState(searchParams.get("q") || "");
  const [selectedCountries, setSelectedCountries] = useState<Set<"dubai" | "saudi" | "qatar">>(new Set());
  const [selectedCategories, setSelectedCategories] = useState<Set<string>>(new Set());
  const [onlyVerified, setOnlyVerified] = useState(false);
  const [onlyDropshipping, setOnlyDropshipping] = useState(false);
  const [maxBulkPrice, setMaxBulkPrice] = useState<number>(2000);
  const [sortBy, setSortBy] = useState<SortOption>("relevance");
  const [showFilters, setShowFilters] = useState(true);

  const toggleCountry = (c: "dubai" | "saudi" | "qatar") => {
    setSelectedCountries((prev) => {
      const next = new Set(prev);
      next.has(c) ? next.delete(c) : next.add(c);
      return next;
    });
  };

  const toggleCategory = (cat: string) => {
    setSelectedCategories((prev) => {
      const next = new Set(prev);
      next.has(cat) ? next.delete(cat) : next.add(cat);
      return next;
    });
  };

  const results = useMemo(() => {
    let list = allProducts.filter((p) => {
      const q = query.toLowerCase();
      const matchesQuery =
        !q ||
        p.name.toLowerCase().includes(q) ||
        p.category.toLowerCase().includes(q) ||
        p.factoryName.toLowerCase().includes(q) ||
        p.factoryCity.toLowerCase().includes(q);
      const matchesCountry = selectedCountries.size === 0 || selectedCountries.has(p.country);
      const matchesCategory = selectedCategories.size === 0 || selectedCategories.has(p.category);
      const matchesVerified = !onlyVerified || p.factoryVerified;
      const matchesDropship = !onlyDropshipping || p.factoryDropshipping;
      const matchesPrice = p.bulkPrice <= maxBulkPrice;
      return matchesQuery && matchesCountry && matchesCategory && matchesVerified && matchesDropship && matchesPrice;
    });

    if (sortBy === "price-asc") list = [...list].sort((a, b) => a.bulkPrice - b.bulkPrice);
    else if (sortBy === "price-desc") list = [...list].sort((a, b) => b.bulkPrice - a.bulkPrice);
    else if (sortBy === "rating") list = [...list].sort((a, b) => b.factoryRating - a.factoryRating);

    return list;
  }, [query, selectedCountries, selectedCategories, onlyVerified, onlyDropshipping, maxBulkPrice, sortBy]);

  const handleSearch = () => setQuery(inputVal);

  const clearAll = () => {
    setQuery("");
    setInputVal("");
    setSelectedCountries(new Set());
    setSelectedCategories(new Set());
    setOnlyVerified(false);
    setOnlyDropshipping(false);
    setMaxBulkPrice(2000);
    setSortBy("relevance");
  };

  const activeFilterCount =
    selectedCountries.size +
    selectedCategories.size +
    (onlyVerified ? 1 : 0) +
    (onlyDropshipping ? 1 : 0) +
    (maxBulkPrice < 2000 ? 1 : 0);

  return (
    <div className="min-h-screen bg-white">
      <SeoHead
        title="Search Middle East Factories & Products | SuperMiddleEast"
        description="Search across Dubai, Saudi Arabia and Qatar factories. Find verified factory products for sample orders, bulk orders and dropshipping. Filter by country, category, price and more."
        keywords="search Middle East factories, find factory products, Dubai factory search, Saudi Arabia products, Qatar manufacturer search, wholesale search"
        canonical={`${BASE_URL}/search`}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "SearchResultsPage",
            "name": "Search Middle East Factories – SuperMiddleEast",
            "description": "Search across verified factories in Dubai, Saudi Arabia and Qatar for products available for sample, bulk and dropshipping orders.",
            "url": `${BASE_URL}/search`,
            "inLanguage": "en",
            "breadcrumb": {
              "@type": "BreadcrumbList",
              "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "Home", "item": BASE_URL },
                { "@type": "ListItem", "position": 2, "name": "Search", "item": `${BASE_URL}/search` },
              ],
            },
          },
        ]}
      />
      <Navbar />
      <div className="pt-24 pb-20">

        {/* Search Hero */}
        <div className="bg-gradient-to-b from-amber-50 to-white px-4 md:px-6 py-10 border-b border-amber-100">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-3xl md:text-4xl font-black text-gray-900 mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
              Search All <span className="text-amber-500">Middle East Factories</span>
            </h1>
            <p className="text-gray-500 text-sm mb-6">Search across Dubai, Saudi Arabia &amp; Qatar — {allProducts.length} products from verified factories</p>
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"></i>
                <input
                  value={inputVal}
                  onChange={(e) => setInputVal(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  placeholder="Search products, categories, factories..."
                  className="w-full pl-11 pr-4 py-3.5 border border-amber-200 rounded-2xl text-sm focus:outline-none focus:border-amber-500 bg-white"
                />
              </div>
              <button
                onClick={handleSearch}
                className="bg-amber-500 hover:bg-amber-600 text-white font-bold px-6 py-3.5 rounded-2xl transition-colors cursor-pointer whitespace-nowrap"
              >
                Search
              </button>
            </div>
            {/* Quick tags */}
            <div className="flex flex-wrap gap-2 justify-center mt-4">
              {["Electronics", "Perfume", "Gold Jewellery", "Dates", "Textiles", "Smart Home"].map((tag) => (
                <button
                  key={tag}
                  onClick={() => { setInputVal(tag); setQuery(tag); }}
                  className="bg-white border border-amber-200 text-amber-700 text-xs font-semibold px-3 py-1.5 rounded-full hover:bg-amber-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 pt-8">
          <div className="flex gap-8">

            {/* Sidebar Filters */}
            <div className={`flex-shrink-0 transition-all ${showFilters ? "w-60" : "w-0 overflow-hidden"}`}>
              <div className="space-y-6">
                {/* Country */}
                <div>
                  <p className="font-bold text-gray-800 text-sm mb-3">Country</p>
                  <div className="space-y-2">
                    {(["dubai", "saudi", "qatar"] as const).map((c) => {
                      const meta = COUNTRY_META[c];
                      return (
                        <label key={c} className="flex items-center gap-2.5 cursor-pointer group">
                          <div
                            onClick={() => toggleCountry(c)}
                            className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all cursor-pointer ${
                              selectedCountries.has(c) ? "bg-amber-500 border-amber-500" : "border-gray-300 group-hover:border-amber-300"
                            }`}
                          >
                            {selectedCountries.has(c) && <i className="ri-check-line text-white text-xs"></i>}
                          </div>
                          <span className="text-sm text-gray-700">{meta.flag} {meta.label}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>

                {/* Verified / Dropshipping */}
                <div>
                  <p className="font-bold text-gray-800 text-sm mb-3">Factory Type</p>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2.5 cursor-pointer group">
                      <div
                        onClick={() => setOnlyVerified(!onlyVerified)}
                        className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all cursor-pointer ${
                          onlyVerified ? "bg-amber-500 border-amber-500" : "border-gray-300 group-hover:border-amber-300"
                        }`}
                      >
                        {onlyVerified && <i className="ri-check-line text-white text-xs"></i>}
                      </div>
                      <span className="text-sm text-gray-700 flex items-center gap-1">
                        <i className="ri-shield-check-fill text-amber-500 text-xs"></i> Verified Only
                      </span>
                    </label>
                    <label className="flex items-center gap-2.5 cursor-pointer group">
                      <div
                        onClick={() => setOnlyDropshipping(!onlyDropshipping)}
                        className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all cursor-pointer ${
                          onlyDropshipping ? "bg-emerald-500 border-emerald-500" : "border-gray-300 group-hover:border-emerald-300"
                        }`}
                      >
                        {onlyDropshipping && <i className="ri-check-line text-white text-xs"></i>}
                      </div>
                      <span className="text-sm text-gray-700 flex items-center gap-1">
                        <i className="ri-truck-fill text-emerald-500 text-xs"></i> Dropshipping
                      </span>
                    </label>
                  </div>
                </div>

                {/* Price Range */}
                <div>
                  <p className="font-bold text-gray-800 text-sm mb-3">Max Bulk Price</p>
                  <input
                    type="range"
                    min={1}
                    max={2000}
                    value={maxBulkPrice}
                    onChange={(e) => setMaxBulkPrice(Number(e.target.value))}
                    className="w-full accent-amber-500"
                  />
                  <div className="flex justify-between text-xs text-gray-400 mt-1">
                    <span>$1</span>
                    <span className="font-bold text-amber-600">${maxBulkPrice}</span>
                    <span>$2000</span>
                  </div>
                </div>

                {/* Categories */}
                <div>
                  <p className="font-bold text-gray-800 text-sm mb-3">Category</p>
                  <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
                    {ALL_CATEGORIES.map((cat) => (
                      <label key={cat} className="flex items-center gap-2.5 cursor-pointer group">
                        <div
                          onClick={() => toggleCategory(cat)}
                          className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all cursor-pointer ${
                            selectedCategories.has(cat) ? "bg-amber-500 border-amber-500" : "border-gray-300 group-hover:border-amber-300"
                          }`}
                        >
                          {selectedCategories.has(cat) && <i className="ri-check-line text-white text-xs"></i>}
                        </div>
                        <span className="text-sm text-gray-700">{cat}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {activeFilterCount > 0 && (
                  <button onClick={clearAll} className="w-full border border-red-200 text-red-500 hover:bg-red-50 font-semibold text-sm py-2 rounded-xl transition-colors cursor-pointer whitespace-nowrap">
                    Clear All Filters ({activeFilterCount})
                  </button>
                )}
              </div>
            </div>

            {/* Results */}
            <div className="flex-1 min-w-0">
              {/* Toolbar */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className="flex items-center gap-1.5 border border-amber-200 text-amber-700 text-sm font-semibold px-3 py-2 rounded-xl hover:bg-amber-50 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className={showFilters ? "ri-filter-off-line" : "ri-filter-line"}></i>
                    {showFilters ? "Hide Filters" : "Show Filters"}
                    {activeFilterCount > 0 && (
                      <span className="bg-amber-500 text-white text-xs w-4 h-4 flex items-center justify-center rounded-full">{activeFilterCount}</span>
                    )}
                  </button>
                  <p className="text-gray-500 text-sm">
                    <strong className="text-gray-900">{results.length}</strong> products found
                    {query && <span> for &quot;<span className="text-amber-600">{query}</span>&quot;</span>}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-400">Sort:</span>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value as SortOption)}
                    className="border border-amber-100 rounded-xl px-3 py-1.5 text-sm focus:outline-none focus:border-amber-400 bg-white cursor-pointer"
                  >
                    <option value="relevance">Relevance</option>
                    <option value="price-asc">Price: Low to High</option>
                    <option value="price-desc">Price: High to Low</option>
                    <option value="rating">Factory Rating</option>
                  </select>
                </div>
              </div>

              {results.length === 0 ? (
                <div className="text-center py-20">
                  <i className="ri-search-line text-5xl text-amber-200 mb-4 block"></i>
                  <p className="text-gray-500 text-lg font-semibold mb-2">No products found</p>
                  <p className="text-gray-400 text-sm mb-4">Try adjusting your filters or search term</p>
                  <button onClick={clearAll} className="bg-amber-500 text-white font-semibold px-5 py-2.5 rounded-xl cursor-pointer whitespace-nowrap hover:bg-amber-600 transition-colors">
                    Clear Filters
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
                  {results.map((product) => {
                    const countryMeta = COUNTRY_META[product.country];
                    return (
                      <Link
                        key={`${product.factoryId}-${product.productId}`}
                        to={`/factory-product/${product.factoryId}/${product.productId}`}
                        className="bg-white border border-amber-100 rounded-2xl overflow-hidden hover:border-amber-300 transition-all cursor-pointer group"
                      >
                        <div className="relative w-full h-44 overflow-hidden">
                          <img
                            src={product.image}
                            alt={product.name}
                            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                          />
                          <div className="absolute top-2 left-2 flex gap-1.5 flex-wrap">
                            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${countryMeta.color}`}>
                              {countryMeta.flag} {countryMeta.label}
                            </span>
                            {product.factoryVerified && (
                              <span className="bg-amber-500 text-white text-xs font-bold px-2 py-0.5 rounded-full flex items-center gap-0.5">
                                <i className="ri-shield-check-fill text-xs"></i> Verified
                              </span>
                            )}
                            {product.factoryDropshipping && (
                              <span className="bg-emerald-500 text-white text-xs font-bold px-2 py-0.5 rounded-full flex items-center gap-0.5">
                                <i className="ri-truck-fill text-xs"></i> Dropship
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="p-4">
                          <span className="bg-amber-100 text-amber-700 text-xs px-2 py-0.5 rounded-full">{product.category}</span>
                          <p className="font-bold text-gray-900 text-sm mt-2 mb-1 leading-tight line-clamp-2">{product.name}</p>
                          <p className="text-gray-400 text-xs mb-3 truncate">{product.factoryName} · {product.factoryCity}</p>
                          <div className="flex items-center justify-between">
                            <div className="flex gap-3 text-xs">
                              <span>Sample: <strong className="text-amber-600">${product.samplePrice}</strong></span>
                              <span>Bulk: <strong className="text-emerald-600">${product.bulkPrice}</strong></span>
                            </div>
                            <div className="flex items-center gap-0.5 text-xs text-gray-500">
                              <i className="ri-star-fill text-amber-400"></i>
                              <span>{product.factoryRating}</span>
                            </div>
                          </div>
                          <div className="text-xs text-gray-400 mt-1">MOQ: {product.moq} units</div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default GlobalSearchPage;
